import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEventsV1Rsvp from './events-v1-rsvp-rsvp.http.js';
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

export interface Rsvp {
  /**
   * RSVP ID.
   * @format GUID
   */
  _id?: string;
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Contact ID associated with this RSVP.
   * @format GUID
   */
  contactId?: string;
  /**
   * Member ID associated with this RSVP.
   * @format GUID
   */
  memberId?: string;
  /** RSVP created timestamp. */
  created?: Date | null;
  /** RSVP modified timestamp. */
  modified?: Date | null;
  /** First name. */
  firstName?: string;
  /** Last name. */
  lastName?: string;
  /**
   * Guest email.
   * @format EMAIL
   */
  email?: string;
  /** RSVP form response. */
  rsvpForm?: FormResponse;
  /**
   * RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   */
  status?: RsvpStatusWithLiterals;
  /** Total number of attendees. */
  totalGuests?: number;
  /** List of guests. */
  guests?: Guest[];
  /** Whether RSVP is anonymized by GDPR delete. */
  anonymized?: boolean;
  /** Whether marketing consent was given */
  marketingConsent?: boolean | null;
}

export interface FormResponse {
  /**
   * Input form fields.
   * @maxSize 200
   */
  inputValues?: InputValue[];
}

export interface InputValue {
  /**
   * Form field name.
   * @maxLength 100
   */
  inputName?: string;
  /**
   * Form field value.
   * @maxLength 5000
   */
  value?: string;
  /**
   * Multiple form field values.
   * @maxSize 100
   * @maxLength 5000
   */
  values?: string[];
}

export interface FormattedAddress {
  /**
   * 1-line address representation.
   * @maxLength 200
   */
  formatted?: string;
  /** Address components. */
  address?: Address;
}

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine1?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision shorthand. Usually, a short code (2 or 3 letters) that represents a state, region, prefecture, or province. e.g. NY */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Subdivision {
  /** Short subdivision code. */
  code?: string;
  /** Subdivision full name. */
  name?: string;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

/** Subdivision Concordance values */
export interface StandardDetails {
  /**
   * subdivision iso-3166-2 code according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). e.g. US-NY, GB-SCT, NO-30
   * @maxLength 20
   */
  iso31662?: string | null;
}

export enum RsvpStatus {
  YES = 'YES',
  NO = 'NO',
  WAITING = 'WAITING',
}

/** @enumType */
export type RsvpStatusWithLiterals = RsvpStatus | 'YES' | 'NO' | 'WAITING';

export interface Guest {
  /** Index in the RSVP guest list. */
  index?: number;
  /** Guest full name. */
  fullName?: string;
  /** Guest check-in. */
  checkIn?: CheckIn;
  /**
   * Unique guest ID per RSVP.
   * @min 1
   */
  _id?: number;
}

export interface CheckIn {
  /** Time of a ticket's check-in. */
  created?: Date | null;
}

export interface ListRsvpRequest {
  /** Number of items to skip. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination). */
  offset?: number;
  /**
   * Number of items to load. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).
   * @max 400
   */
  limit?: number;
  /**
   * Set of fields to return. Possible values:
   * - `DETAILS`: Returns `created`, `modified`, `firstName`, `lastName` fields.
   * - `FORM`: Returns `rsvpForm` field.
   * - `CONTACT_DETAILS`: Returns `email` field.
   * @maxSize 20
   */
  fieldset?: RsvpFieldsetWithLiterals[];
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   * @maxSize 100
   */
  eventId?: string[];
  /**
   * RSVP ID.
   * @format GUID
   * @maxSize 500
   */
  rsvpId?: string[];
  /**
   * RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   * @maxSize 20
   */
  status?: RsvpStatusWithLiterals[];
  /**
   * Site member ID.
   * @format GUID
   * @maxSize 500
   */
  memberId?: string[];
  /**
   * Facet counts to include in the response.
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
  /**
   * Textual search filter - search is performed on "full_name" and "email".
   * @maxLength 200
   */
  searchPhrase?: string;
  /**
   * Event creator id filter, by default any.
   * @format GUID
   * @maxSize 50
   */
  eventCreatorId?: string[];
  /**
   * Sort order, defaults to `"created:asc"`.
   * @maxLength 100
   */
  sort?: string;
  /**
   * Contact ID.
   * @format GUID
   * @maxSize 100
   */
  contactId?: string[];
  /** RSVP tag */
  tag?: RsvpTagWithLiterals[];
}

export enum RsvpFieldset {
  /** Include RSVP details including: `created`, `modified`, `firstName`, `lastName`, `status`, `totalGuests`, `guests`, and `annonymized`. */
  DETAILS = 'DETAILS',
  /** Include RSVP form. */
  FORM = 'FORM',
  /** Include RSVP email. */
  CONTACT_DETAILS = 'CONTACT_DETAILS',
}

/** @enumType */
export type RsvpFieldsetWithLiterals =
  | RsvpFieldset
  | 'DETAILS'
  | 'FORM'
  | 'CONTACT_DETAILS';

export enum RsvpTag {
  /** Return only RSVPs of all guests that are fully checked-in. */
  FULLY_CHECKED_IN = 'FULLY_CHECKED_IN',
  /** Return only RSVPs of all guests that aren't fully checked-in. */
  NOT_FULLY_CHECKED_IN = 'NOT_FULLY_CHECKED_IN',
  /** Return only RSVPs of guests that are members. */
  MEMBER = 'MEMBER',
}

/** @enumType */
export type RsvpTagWithLiterals =
  | RsvpTag
  | 'FULLY_CHECKED_IN'
  | 'NOT_FULLY_CHECKED_IN'
  | 'MEMBER';

export interface ListRsvpResponse {
  /** Total RSVPs matching the given filters. */
  total?: number;
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   * @max 100
   */
  limit?: number;
  /** RSVP list. */
  rsvps?: Rsvp[];
  /** Facet query result. */
  facets?: Record<string, FacetCounts>;
  /** Rsvp data enriched facets. */
  rsvpFacets?: RsvpFacets;
}

export interface FacetCounts {
  /** Facet counts aggregated per value. */
  counts?: Record<string, number>;
}

export interface RsvpFacets {
  /** Filter facets. */
  facets?: Record<string, RsvpFacetCounts>;
}

export interface RsvpFacetCounts {
  /** Facet totals, aggregated per filter. */
  counts?: Record<string, Counts>;
}

export interface Counts {
  /** Number of RSVPs. */
  count?: number;
  /** Number of guests within RSVPs. */
  guests?: number;
  /** Number of guests who have checked-in. */
  guestsCheckIn?: number;
}

export interface QueryRsvpRequest {
  /** Offset. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination). */
  offset?: number;
  /**
   * Limit. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).
   * @max 100
   */
  limit?: number;
  /**
   * Set of fields to return. Possible values:
   * - `DETAILS`: Returns `created`, `modified`, `firstName`, `lastName` fields.
   * - `FORM`: Returns `rsvpForm` field.
   * - `CONTACT_DETAILS`: Returns `email` field.
   * @maxSize 20
   */
  fieldset?: RsvpFieldsetWithLiterals[];
  /** Filter. */
  filter?: Record<string, any> | null;
  /**
   * Site member ID.
   * @format GUID
   * @maxSize 500
   */
  memberId?: string[];
  /**
   * Filter facets to include in the response.
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
  /**
   * Textual search filter - search is performed on "guests.full_name".
   * @maxLength 200
   */
  searchPhrase?: string;
  /**
   * Event creator ID.
   * @format GUID
   * @maxSize 50
   */
  eventCreatorId?: string[];
  /**
   * Sort order, defaults to `"created:asc"`.
   * @maxLength 100
   */
  sort?: string;
  /**
   * Contact ID.
   * @format GUID
   * @maxSize 100
   */
  contactId?: string[];
  /** RSVP tag */
  tag?: RsvpTagWithLiterals[];
}

export interface QueryRsvpResponse {
  /** Total RSVPs matching the given filters. */
  total?: number;
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   * @max 100
   */
  limit?: number;
  /** RSVP list. */
  rsvps?: Rsvp[];
  /** Facet query result. */
  facets?: Record<string, FacetCounts>;
  /** Rsvp data enriched facets. */
  rsvpFacets?: RsvpFacets;
}

export interface GetRsvpRequest {
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * RSVP ID.
   * @format GUID
   */
  rsvpId: string;
  /**
   * Set of fields to return. Possible values:
   * - `DETAILS`: Returns `created`, `modified`, `firstName`, `lastName` fields.
   * - `FORM`: Returns `rsvpForm` field.
   * - `CONTACT_DETAILS`: Returns `email` field.
   * @maxSize 20
   */
  fieldset?: RsvpFieldsetWithLiterals[];
}

export interface GetRsvpResponse {
  /** RSVP. */
  rsvp?: Rsvp;
}

export interface CreateRsvpRequest {
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId?: string;
  /** RSVP form response. */
  form?: FormResponse;
  /**
   * RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   */
  status?: RsvpStatusWithLiterals;
  /**
   * Member ID of the RSVP.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Add RSVP options.
   * **Note:** WIX_EVENTS.MANAGE_RSVP permission is required.
   */
  options?: ModificationOptions;
  /** Whether marketing consent was given */
  marketingConsent?: boolean | null;
}

export interface ModificationOptions {
  /** Whether to ignore notification settings (when hen true, no notifications to contact or user are sent). */
  silent?: boolean;
  /** Whether to create/update regardless of event guest limit. */
  ignoreLimits?: boolean;
  /** Whether to ignore the form validation. */
  ignoreFormValidation?: boolean;
}

export interface CreateRsvpResponse {
  /** Created RSVP. */
  rsvp?: Rsvp;
  /** "Add to calendar" links. */
  calendarLinks?: CalendarLinks;
}

export interface CalendarLinks {
  /** "Add to Google calendar" URL. */
  google?: string;
  /** "Download ICS calendar file" URL. */
  ics?: string;
}

export interface RsvpCreated {
  /** RSVP created timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Site language when RSVP created
   * @format LANGUAGE
   */
  language?: string | null;
  /** Notifications silenced for this domain event. */
  silent?: boolean | null;
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * RSVP ID.
   * @format GUID
   */
  rsvpId?: string;
  /** Contact ID associated with this RSVP. */
  contactId?: string;
  /**
   * Member ID associated with this RSVP.
   * @format GUID
   */
  memberId?: string | null;
  /** Guest first name. */
  firstName?: string;
  /** Guest last name. */
  lastName?: string;
  /**
   * Guest email.
   * @format EMAIL
   */
  email?: string;
  /** RSVP form response. */
  rsvpForm?: FormResponse;
  /**
   * RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   */
  status?: RsvpStatusWithLiterals;
  /** List of all guests. */
  guests?: Guest[];
  /** URL and password to online conference */
  onlineConferencingLogin?: OnlineConferencingLogin;
}

export interface OnlineConferencingLogin {
  /**
   * Link URL to the online conference.
   * @format WEB_URL
   * @readonly
   */
  link?: string;
  /**
   * Password for the online conference.
   * @readonly
   */
  password?: string | null;
}

export interface UpdateRsvpRequest {
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * RSVP ID.
   * @format GUID
   */
  rsvpId: string;
  /** Set of field paths, specifying which parts of RSVP to update. */
  fields: string[];
  /** RSVP form response. */
  rsvpForm?: FormResponse;
  /**
   * RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   */
  status?: RsvpStatusWithLiterals;
  /**
   * Update RSVP options.
   * WIX_EVENTS.MANAGE_RSVP permission is required.
   */
  options?: ModificationOptions;
}

export interface UpdateRsvpResponse {
  /** Updated RSVP. */
  rsvp?: Rsvp;
}

export interface RsvpUpdated {
  /** RSVP updated timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Site language when RSVP created
   * @format LANGUAGE
   */
  language?: string | null;
  /**
   * Locale in which Rsvp was created.
   * @format LANGUAGE_TAG
   */
  locale?: string | null;
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * RSVP ID.
   * @format GUID
   */
  rsvpId?: string;
  /** Contact ID associated with this RSVP. */
  contactId?: string;
  /**
   * Member ID associated with this RSVP.
   * @format GUID
   */
  memberId?: string | null;
  /** RSVP created timestamp. */
  created?: Date | null;
  /** Guest first name. */
  firstName?: string;
  /** Guest last name. */
  lastName?: string;
  /**
   * Guest email.
   * @format EMAIL
   */
  email?: string;
  /** RSVP form response. */
  rsvpForm?: FormResponse;
  /**
   * RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   */
  status?: RsvpStatusWithLiterals;
  /** List of the guests. */
  guests?: Guest[];
  /** URL and password to online conference */
  onlineConferencingLogin?: OnlineConferencingLogin;
  /** Notifications silenced for this domain event. */
  silent?: boolean | null;
}

export interface BulkUpdateRsvpRequest {
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * RSVPs to update.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  rsvpId?: string[];
  /** Set of fields to update. */
  fields?: string[];
  /**
   * New RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   */
  status?: RsvpStatusWithLiterals;
}

export interface BulkUpdateRsvpResponse {
  /** Updated RSVPs. */
  rsvps?: Rsvp[];
}

export interface DeleteRsvpRequest {
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * RSVPs to delete.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  rsvps?: string[];
}

export interface DeleteRsvpResponse {}

export interface RsvpDeleted {
  /** RSVP deleted timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * RSVP ID.
   * @format GUID
   */
  rsvpId?: string;
  /** Contact ID associated with this RSVP. */
  contactId?: string;
  /**
   * Member ID associated with this RSVP.
   * @format GUID
   */
  memberId?: string | null;
  /** Whether RSVP was anonymized by GDPR delete. */
  anonymized?: boolean;
}

export interface CheckInRsvpRequest {
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * RSVP ID to check-in.
   * @format GUID
   */
  rsvpId: string;
  /**
   * Guest IDs to check-in.
   * @min 1
   * @minSize 1
   * @maxSize 11
   */
  guestId?: number[];
}

export interface CheckInRsvpResponse {
  /** Updated RSVP. */
  rsvp?: Rsvp;
}

export interface DeleteRsvpCheckInRequest {
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId: string;
  /**
   * RSVP ID to delete check-in.
   * @format GUID
   */
  rsvpId: string;
  /**
   * Guest IDs to delete check-in.
   * @min 1
   * @minSize 1
   * @maxSize 11
   */
  guestId?: number[];
}

export interface DeleteRsvpCheckInResponse {
  /** Updated RSVP. */
  rsvp?: Rsvp;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface RsvpCreatedEnvelope {
  data: RsvpCreated;
  metadata: BaseEventMetadata;
}

/** @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Guest List
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-GUEST-LIST
 * @permissionScope Read Event Tickets and Guest List
 * @permissionScopeId SCOPE.DC-EVENTS.READ-GUEST-LIST
 * @permissionId WIX_EVENTS.READ_RSVP
 * @webhook
 * @eventType wix.events.rsvp.events.RsvpCreated
 * @serviceIdentifier wix.events.rsvp.RsvpManagement
 * @slug rsvp_created
 * @deprecated
 */
export declare function onRsvpCreated(
  handler: (event: RsvpCreatedEnvelope) => void | Promise<void>
): void;

export interface RsvpDeletedEnvelope {
  data: RsvpDeleted;
  metadata: BaseEventMetadata;
}

/** @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Guest List
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-GUEST-LIST
 * @permissionScope Read Event Tickets and Guest List
 * @permissionScopeId SCOPE.DC-EVENTS.READ-GUEST-LIST
 * @permissionId WIX_EVENTS.READ_RSVP
 * @webhook
 * @eventType wix.events.rsvp.events.RsvpDeleted
 * @serviceIdentifier wix.events.rsvp.RsvpManagement
 * @slug rsvp_deleted
 * @deprecated
 */
export declare function onRsvpDeleted(
  handler: (event: RsvpDeletedEnvelope) => void | Promise<void>
): void;

export interface RsvpUpdatedEnvelope {
  data: RsvpUpdated;
  metadata: BaseEventMetadata;
}

/** @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Guest List
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-GUEST-LIST
 * @permissionScope Read Event Tickets and Guest List
 * @permissionScopeId SCOPE.DC-EVENTS.READ-GUEST-LIST
 * @permissionId WIX_EVENTS.READ_RSVP
 * @webhook
 * @eventType wix.events.rsvp.events.RsvpUpdated
 * @serviceIdentifier wix.events.rsvp.RsvpManagement
 * @slug rsvp_updated
 * @deprecated
 */
export declare function onRsvpUpdated(
  handler: (event: RsvpUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves a list of up to 100 RSVPs.
 * @public
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.READ_RSVP
 * @applicableIdentity APP
 * @fqn wix.events.rsvp.RsvpManagement.ListRsvp
 * @deprecated
 * @replacedBy wix.events.rsvps.v2.RsvpManagement.QueryRsvps
 * @targetRemovalDate 2025-06-30
 */
export async function listRsvp(
  options?: ListRsvpOptions
): Promise<
  NonNullablePaths<
    ListRsvpResponse,
    | `total`
    | `offset`
    | `limit`
    | `rsvps`
    | `rsvps.${number}._id`
    | `rsvps.${number}.eventId`
    | `rsvps.${number}.contactId`
    | `rsvps.${number}.memberId`
    | `rsvps.${number}.firstName`
    | `rsvps.${number}.lastName`
    | `rsvps.${number}.email`
    | `rsvps.${number}.status`
    | `rsvps.${number}.totalGuests`
    | `rsvps.${number}.anonymized`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    offset: options?.offset,
    limit: options?.limit,
    fieldset: options?.fieldset,
    eventId: options?.eventId,
    rsvpId: options?.rsvpId,
    status: options?.status,
    memberId: options?.memberId,
    facet: options?.facet,
    searchPhrase: options?.searchPhrase,
    eventCreatorId: options?.eventCreatorId,
    sort: options?.sort,
    contactId: options?.contactId,
    tag: options?.tag,
  });

  const reqOpts = ambassadorWixEventsV1Rsvp.listRsvp(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'rsvps.rsvpForm.inputValues.address.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          offset: '$[0].offset',
          limit: '$[0].limit',
          fieldset: '$[0].fieldset',
          eventId: '$[0].eventId',
          rsvpId: '$[0].rsvpId',
          status: '$[0].status',
          memberId: '$[0].memberId',
          facet: '$[0].facet',
          searchPhrase: '$[0].searchPhrase',
          eventCreatorId: '$[0].eventCreatorId',
          sort: '$[0].sort',
          contactId: '$[0].contactId',
          tag: '$[0].tag',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListRsvpOptions {
  /** Number of items to skip. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination). */
  offset?: number;
  /**
   * Number of items to load. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).
   * @max 400
   */
  limit?: number;
  /**
   * Set of fields to return. Possible values:
   * - `DETAILS`: Returns `created`, `modified`, `firstName`, `lastName` fields.
   * - `FORM`: Returns `rsvpForm` field.
   * - `CONTACT_DETAILS`: Returns `email` field.
   * @maxSize 20
   */
  fieldset?: RsvpFieldsetWithLiterals[];
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   * @maxSize 100
   */
  eventId?: string[];
  /**
   * RSVP ID.
   * @format GUID
   * @maxSize 500
   */
  rsvpId?: string[];
  /**
   * RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   * @maxSize 20
   */
  status?: RsvpStatusWithLiterals[];
  /**
   * Site member ID.
   * @format GUID
   * @maxSize 500
   */
  memberId?: string[];
  /**
   * Facet counts to include in the response.
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
  /**
   * Textual search filter - search is performed on "full_name" and "email".
   * @maxLength 200
   */
  searchPhrase?: string;
  /**
   * Event creator id filter, by default any.
   * @format GUID
   * @maxSize 50
   */
  eventCreatorId?: string[];
  /**
   * Sort order, defaults to `"created:asc"`.
   * @maxLength 100
   */
  sort?: string;
  /**
   * Contact ID.
   * @format GUID
   * @maxSize 100
   */
  contactId?: string[];
  /** RSVP tag */
  tag?: RsvpTagWithLiterals[];
}

/**
 * Retrieves a list of up to 100 RSVPs.
 * @public
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.READ_RSVP
 * @applicableIdentity APP
 * @fqn wix.events.rsvp.RsvpManagement.QueryRsvp
 * @deprecated
 * @replacedBy wix.events.rsvps.v2.RsvpManagement.QueryRsvps
 * @targetRemovalDate 2025-06-30
 */
export async function queryRsvp(
  options?: QueryRsvpOptions
): Promise<
  NonNullablePaths<
    QueryRsvpResponse,
    | `total`
    | `offset`
    | `limit`
    | `rsvps`
    | `rsvps.${number}._id`
    | `rsvps.${number}.eventId`
    | `rsvps.${number}.contactId`
    | `rsvps.${number}.memberId`
    | `rsvps.${number}.firstName`
    | `rsvps.${number}.lastName`
    | `rsvps.${number}.email`
    | `rsvps.${number}.status`
    | `rsvps.${number}.totalGuests`
    | `rsvps.${number}.anonymized`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    offset: options?.offset,
    limit: options?.limit,
    fieldset: options?.fieldset,
    filter: options?.filter,
    memberId: options?.memberId,
    facet: options?.facet,
    searchPhrase: options?.searchPhrase,
    eventCreatorId: options?.eventCreatorId,
    sort: options?.sort,
    contactId: options?.contactId,
    tag: options?.tag,
  });

  const reqOpts = ambassadorWixEventsV1Rsvp.queryRsvp(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'rsvps.rsvpForm.inputValues.address.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          offset: '$[0].offset',
          limit: '$[0].limit',
          fieldset: '$[0].fieldset',
          filter: '$[0].filter',
          memberId: '$[0].memberId',
          facet: '$[0].facet',
          searchPhrase: '$[0].searchPhrase',
          eventCreatorId: '$[0].eventCreatorId',
          sort: '$[0].sort',
          contactId: '$[0].contactId',
          tag: '$[0].tag',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryRsvpOptions {
  /** Offset. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination). */
  offset?: number;
  /**
   * Limit. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).
   * @max 100
   */
  limit?: number;
  /**
   * Set of fields to return. Possible values:
   * - `DETAILS`: Returns `created`, `modified`, `firstName`, `lastName` fields.
   * - `FORM`: Returns `rsvpForm` field.
   * - `CONTACT_DETAILS`: Returns `email` field.
   * @maxSize 20
   */
  fieldset?: RsvpFieldsetWithLiterals[];
  /** Filter. */
  filter?: Record<string, any> | null;
  /**
   * Site member ID.
   * @format GUID
   * @maxSize 500
   */
  memberId?: string[];
  /**
   * Filter facets to include in the response.
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
  /**
   * Textual search filter - search is performed on "guests.full_name".
   * @maxLength 200
   */
  searchPhrase?: string;
  /**
   * Event creator ID.
   * @format GUID
   * @maxSize 50
   */
  eventCreatorId?: string[];
  /**
   * Sort order, defaults to `"created:asc"`.
   * @maxLength 100
   */
  sort?: string;
  /**
   * Contact ID.
   * @format GUID
   * @maxSize 100
   */
  contactId?: string[];
  /** RSVP tag */
  tag?: RsvpTagWithLiterals[];
}

/**
 * Retrieves an RSVP.
 * @param rsvpId - RSVP ID.
 * @public
 * @requiredField rsvpId
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.READ_RSVP
 * @applicableIdentity APP
 * @returns RSVP.
 * @fqn wix.events.rsvp.RsvpManagement.GetRsvp
 * @deprecated
 * @replacedBy wix.events.rsvps.v2.RsvpManagement.GetRsvp
 * @targetRemovalDate 2025-06-30
 */
export async function getRsvp(
  rsvpId: string,
  options?: GetRsvpOptions
): Promise<
  NonNullablePaths<
    Rsvp,
    | `_id`
    | `eventId`
    | `contactId`
    | `memberId`
    | `firstName`
    | `lastName`
    | `email`
    | `rsvpForm.inputValues`
    | `rsvpForm.inputValues.${number}.inputName`
    | `rsvpForm.inputValues.${number}.value`
    | `status`
    | `totalGuests`
    | `guests`
    | `guests.${number}.index`
    | `guests.${number}.fullName`
    | `guests.${number}._id`
    | `anonymized`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    rsvpId: rsvpId,
    eventId: options?.eventId,
    fieldset: options?.fieldset,
  });

  const reqOpts = ambassadorWixEventsV1Rsvp.getRsvp(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'rsvp.rsvpForm.inputValues.address.address' }],
        },
      ])
    )?.rsvp!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          rsvpId: '$[0]',
          eventId: '$[1].eventId',
          fieldset: '$[1].fieldset',
        },
        singleArgumentUnchanged: false,
      },
      ['rsvpId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetRsvpOptions {
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Set of fields to return. Possible values:
   * - `DETAILS`: Returns `created`, `modified`, `firstName`, `lastName` fields.
   * - `FORM`: Returns `rsvpForm` field.
   * - `CONTACT_DETAILS`: Returns `email` field.
   * @maxSize 20
   */
  fieldset?: RsvpFieldsetWithLiterals[];
}

/**
 * Creates an RSVP, associated with a contact of the site.
 * @public
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.CREATE_RSVP
 * @applicableIdentity APP
 * @fqn wix.events.rsvp.RsvpManagement.CreateRsvp
 * @deprecated
 * @replacedBy wix.events.rsvps.v2.RsvpManagement.CreateRsvp
 * @targetRemovalDate 2025-06-30
 */
export async function createRsvp(
  options?: CreateRsvpOptions
): Promise<
  NonNullablePaths<
    CreateRsvpResponse,
    | `rsvp._id`
    | `rsvp.eventId`
    | `rsvp.contactId`
    | `rsvp.memberId`
    | `rsvp.firstName`
    | `rsvp.lastName`
    | `rsvp.email`
    | `rsvp.rsvpForm.inputValues`
    | `rsvp.rsvpForm.inputValues.${number}.inputName`
    | `rsvp.rsvpForm.inputValues.${number}.value`
    | `rsvp.status`
    | `rsvp.totalGuests`
    | `rsvp.guests`
    | `rsvp.guests.${number}.index`
    | `rsvp.guests.${number}.fullName`
    | `rsvp.guests.${number}._id`
    | `rsvp.anonymized`
    | `calendarLinks.google`
    | `calendarLinks.ics`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      eventId: options?.eventId,
      form: options?.form,
      status: options?.status,
      memberId: options?.memberId,
      options: options?.options,
      marketingConsent: options?.marketingConsent,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'form.inputValues.address.address' }],
      },
    ]
  );

  const reqOpts = ambassadorWixEventsV1Rsvp.createRsvp(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'rsvp.rsvpForm.inputValues.address.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0].eventId',
          form: '$[0].form',
          status: '$[0].status',
          memberId: '$[0].memberId',
          options: '$[0].options',
          marketingConsent: '$[0].marketingConsent',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateRsvpOptions {
  /**
   * Event ID to which RSVP belongs.
   * @format GUID
   */
  eventId?: string;
  /** RSVP form response. */
  form?: FormResponse;
  /**
   * RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   */
  status?: RsvpStatusWithLiterals;
  /**
   * Member ID of the RSVP.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Add RSVP options.
   * **Note:** WIX_EVENTS.MANAGE_RSVP permission is required.
   */
  options?: ModificationOptions;
  /** Whether marketing consent was given */
  marketingConsent?: boolean | null;
}

/**
 * Updates an RSVP.
 * @param rsvpId - RSVP ID.
 * @public
 * @requiredField eventId
 * @requiredField options.fields
 * @requiredField rsvpId
 * @param eventId - Event ID to which RSVP belongs.
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.MANAGE_RSVP
 * @applicableIdentity APP
 * @fqn wix.events.rsvp.RsvpManagement.UpdateRsvp
 * @deprecated
 * @replacedBy wix.events.rsvps.v2.RsvpManagement.UpdateRsvp
 * @targetRemovalDate 2025-06-30
 */
export async function updateRsvp(
  rsvpId: string,
  eventId: string,
  options?: NonNullablePaths<UpdateRsvpOptions, `fields`, 2>
): Promise<
  NonNullablePaths<
    UpdateRsvpResponse,
    | `rsvp._id`
    | `rsvp.eventId`
    | `rsvp.contactId`
    | `rsvp.memberId`
    | `rsvp.firstName`
    | `rsvp.lastName`
    | `rsvp.email`
    | `rsvp.rsvpForm.inputValues`
    | `rsvp.rsvpForm.inputValues.${number}.inputName`
    | `rsvp.rsvpForm.inputValues.${number}.value`
    | `rsvp.status`
    | `rsvp.totalGuests`
    | `rsvp.guests`
    | `rsvp.guests.${number}.index`
    | `rsvp.guests.${number}.fullName`
    | `rsvp.guests.${number}._id`
    | `rsvp.anonymized`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      rsvpId: rsvpId,
      eventId: eventId,
      fields: options?.fields,
      rsvpForm: options?.rsvpForm,
      status: options?.status,
      options: options?.options,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'rsvpForm.inputValues.address.address' }],
      },
    ]
  );

  const reqOpts = ambassadorWixEventsV1Rsvp.updateRsvp(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'rsvp.rsvpForm.inputValues.address.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          rsvpId: '$[0]',
          eventId: '$[1]',
          fields: '$[2].fields',
          rsvpForm: '$[2].rsvpForm',
          status: '$[2].status',
          options: '$[2].options',
        },
        singleArgumentUnchanged: false,
      },
      ['rsvpId', 'eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateRsvpOptions {
  /** Set of field paths, specifying which parts of RSVP to update. */
  fields: string[];
  /** RSVP form response. */
  rsvpForm?: FormResponse;
  /**
   * RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   */
  status?: RsvpStatusWithLiterals;
  /**
   * Update RSVP options.
   * WIX_EVENTS.MANAGE_RSVP permission is required.
   */
  options?: ModificationOptions;
}

/**
 * Updates statuses of multiple RSVPs.
 * @public
 * @requiredField eventId
 * @param eventId - Event ID to which RSVP belongs.
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.MANAGE_RSVP
 * @applicableIdentity APP
 * @fqn wix.events.rsvp.RsvpManagement.BulkUpdateRsvp
 * @deprecated
 * @replacedBy wix.events.rsvps.v2.RsvpManagement.BulkUpdateRsvps
 * @targetRemovalDate 2025-06-30
 */
export async function bulkUpdateRsvp(
  eventId: string,
  options?: BulkUpdateRsvpOptions
): Promise<
  NonNullablePaths<
    BulkUpdateRsvpResponse,
    | `rsvps`
    | `rsvps.${number}._id`
    | `rsvps.${number}.eventId`
    | `rsvps.${number}.contactId`
    | `rsvps.${number}.memberId`
    | `rsvps.${number}.firstName`
    | `rsvps.${number}.lastName`
    | `rsvps.${number}.email`
    | `rsvps.${number}.status`
    | `rsvps.${number}.totalGuests`
    | `rsvps.${number}.anonymized`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    rsvpId: options?.rsvpId,
    fields: options?.fields,
    status: options?.status,
  });

  const reqOpts = ambassadorWixEventsV1Rsvp.bulkUpdateRsvp(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'rsvps.rsvpForm.inputValues.address.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          rsvpId: '$[1].rsvpId',
          fields: '$[1].fields',
          status: '$[1].status',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateRsvpOptions {
  /**
   * RSVPs to update.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  rsvpId?: string[];
  /** Set of fields to update. */
  fields?: string[];
  /**
   * New RSVP response status. Possible values:
   * - `Yes`
   * - `No`
   * - `Waiting`: a guest is in the waitlist.
   */
  status?: RsvpStatusWithLiterals;
}

/**
 * Deletes an RSVP.
 * @public
 * @requiredField eventId
 * @param eventId - Event ID to which RSVP belongs.
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.MANAGE_RSVP
 * @applicableIdentity APP
 * @fqn wix.events.rsvp.RsvpManagement.DeleteRsvp
 * @deprecated
 * @replacedBy wix.events.rsvps.v2.RsvpManagement.DeleteRsvp
 * @targetRemovalDate 2025-06-30
 */
export async function deleteRsvp(
  eventId: string,
  options?: DeleteRsvpOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    rsvps: options?.rsvps,
  });

  const reqOpts = ambassadorWixEventsV1Rsvp.deleteRsvp(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { eventId: '$[0]', rsvps: '$[1].rsvps' },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteRsvpOptions {
  /**
   * RSVPs to delete. When not defined, all RSVPs associated with this event are deleted.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  rsvps?: string[];
}

/**
 * Checks-in an RSVP.
 * @public
 * @requiredField eventId
 * @requiredField options.rsvpId
 * @param eventId - Event ID to which RSVP belongs.
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.CHECK_IN
 * @applicableIdentity APP
 * @fqn wix.events.rsvp.RsvpManagement.CheckInRsvp
 * @deprecated
 * @replacedBy wix.events.rsvps.v2.RsvpManagement.CheckInRsvpGuests
 * @targetRemovalDate 2025-06-30
 */
export async function checkInRsvp(
  eventId: string,
  options?: NonNullablePaths<CheckInRsvpOptions, `rsvpId`, 2>
): Promise<
  NonNullablePaths<
    CheckInRsvpResponse,
    | `rsvp._id`
    | `rsvp.eventId`
    | `rsvp.contactId`
    | `rsvp.memberId`
    | `rsvp.firstName`
    | `rsvp.lastName`
    | `rsvp.email`
    | `rsvp.rsvpForm.inputValues`
    | `rsvp.rsvpForm.inputValues.${number}.inputName`
    | `rsvp.rsvpForm.inputValues.${number}.value`
    | `rsvp.status`
    | `rsvp.totalGuests`
    | `rsvp.guests`
    | `rsvp.guests.${number}.index`
    | `rsvp.guests.${number}.fullName`
    | `rsvp.guests.${number}._id`
    | `rsvp.anonymized`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    rsvpId: options?.rsvpId,
    guestId: options?.guestId,
  });

  const reqOpts = ambassadorWixEventsV1Rsvp.checkInRsvp(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'rsvp.rsvpForm.inputValues.address.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          rsvpId: '$[1].rsvpId',
          guestId: '$[1].guestId',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CheckInRsvpOptions {
  /**
   * RSVP ID to check-in.
   * @format GUID
   */
  rsvpId: string;
  /**
   * Guest IDs to check-in.
   * @min 1
   * @minSize 1
   * @maxSize 11
   */
  guestId?: number[];
}

/**
 * Deletes an RSVP check-in.
 * @public
 * @requiredField eventId
 * @requiredField options.rsvpId
 * @param eventId - Event ID to which RSVP belongs.
 * @param options - Optional fields.
 * @permissionId WIX_EVENTS.CHECK_IN
 * @applicableIdentity APP
 * @fqn wix.events.rsvp.RsvpManagement.DeleteRsvpCheckIn
 * @deprecated
 * @replacedBy wix.events.rsvps.v2.RsvpManagement.CancelRsvpGuestsCheckIn
 * @targetRemovalDate 2025-06-30
 */
export async function deleteRsvpCheckIn(
  eventId: string,
  options?: NonNullablePaths<DeleteRsvpCheckInOptions, `rsvpId`, 2>
): Promise<
  NonNullablePaths<
    DeleteRsvpCheckInResponse,
    | `rsvp._id`
    | `rsvp.eventId`
    | `rsvp.contactId`
    | `rsvp.memberId`
    | `rsvp.firstName`
    | `rsvp.lastName`
    | `rsvp.email`
    | `rsvp.rsvpForm.inputValues`
    | `rsvp.rsvpForm.inputValues.${number}.inputName`
    | `rsvp.rsvpForm.inputValues.${number}.value`
    | `rsvp.status`
    | `rsvp.totalGuests`
    | `rsvp.guests`
    | `rsvp.guests.${number}.index`
    | `rsvp.guests.${number}.fullName`
    | `rsvp.guests.${number}._id`
    | `rsvp.anonymized`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    rsvpId: options?.rsvpId,
    guestId: options?.guestId,
  });

  const reqOpts = ambassadorWixEventsV1Rsvp.deleteRsvpCheckIn(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'rsvp.rsvpForm.inputValues.address.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          rsvpId: '$[1].rsvpId',
          guestId: '$[1].guestId',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteRsvpCheckInOptions {
  /**
   * RSVP ID to delete check-in.
   * @format GUID
   */
  rsvpId: string;
  /**
   * Guest IDs to delete check-in.
   * @min 1
   * @minSize 1
   * @maxSize 11
   */
  guestId?: number[];
}
